import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, BarChart, Bar } from 'recharts';

interface SustainabilityChartsProps {
  monthlyData: Array<{
    month: string;
    products: number;
    saferIngredients: number;
    carbon: number;
    water: number;
  }>;
  riskLevels: Array<{
    level: string;
    count: number;
    percentage: number;
  }>;
  productTypes: Array<{
    type: string;
    count: number;
    saferPercentage: number;
  }>;
  certificationStatus: Array<{
    status: string;
    count: number;
    percentage: number;
  }>;
}

const COLORS = {
  primary: '#8b5cf6',
  secondary: '#06b6d4',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444',
  low: '#10b981',
  moderate: '#f59e0b',
  high: '#ef4444',
  unknown: '#6b7280',
};

export const SustainabilityCharts = ({ 
  monthlyData, 
  riskLevels, 
  productTypes, 
  certificationStatus 
}: SustainabilityChartsProps) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Monthly Trends Chart */}
      <Card className="lg:col-span-2">
        <CardHeader>
          <CardTitle>Sustainability Trends</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis 
                dataKey="month" 
                className="text-xs"
                tick={{ fontSize: 12 }}
              />
              <YAxis className="text-xs" tick={{ fontSize: 12 }} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--background))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '6px'
                }}
              />
              <Line 
                type="monotone" 
                dataKey="products" 
                stroke={COLORS.primary} 
                strokeWidth={2}
                name="Products Added"
              />
              <Line 
                type="monotone" 
                dataKey="saferIngredients" 
                stroke={COLORS.success} 
                strokeWidth={2}
                name="Safer Ingredients"
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Environmental Impact Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Environmental Impact</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart data={monthlyData}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis 
                dataKey="month" 
                className="text-xs"
                tick={{ fontSize: 11 }}
              />
              <YAxis className="text-xs" tick={{ fontSize: 11 }} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--background))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '6px'
                }}
              />
              <Area 
                type="monotone" 
                dataKey="carbon" 
                stackId="1"
                stroke={COLORS.secondary} 
                fill={COLORS.secondary}
                fillOpacity={0.6}
                name="Carbon Reduced (kg)"
              />
              <Area 
                type="monotone" 
                dataKey="water" 
                stackId="2"
                stroke={COLORS.primary} 
                fill={COLORS.primary}
                fillOpacity={0.6}
                name="Water Saved (L)"
              />
            </AreaChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Risk Level Distribution */}
      <Card>
        <CardHeader>
          <CardTitle>Chemical Risk Distribution</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={riskLevels}
                cx="50%"
                cy="50%"
                innerRadius={40}
                outerRadius={80}
                paddingAngle={5}
                dataKey="count"
                label={({ level, percentage }) => `${level}: ${percentage}%`}
                labelLine={false}
                fontSize={11}
              >
                {riskLevels.map((entry, index) => (
                  <Cell 
                    key={`cell-${index}`} 
                    fill={COLORS[entry.level.toLowerCase() as keyof typeof COLORS] || COLORS.unknown} 
                  />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--background))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '6px'
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Product Type Performance */}
      <Card>
        <CardHeader>
          <CardTitle>Product Type Performance</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={productTypes} layout="horizontal">
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis 
                type="number" 
                className="text-xs"
                tick={{ fontSize: 11 }}
              />
              <YAxis 
                type="category" 
                dataKey="type" 
                className="text-xs"
                tick={{ fontSize: 11 }}
                width={80}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--background))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '6px'
                }}
                formatter={(value, name) => [
                  `${value}${name === 'saferPercentage' ? '%' : ''}`,
                  name === 'saferPercentage' ? 'Safer %' : 'Count'
                ]}
              />
              <Bar 
                dataKey="saferPercentage" 
                fill={COLORS.success}
                name="saferPercentage"
                radius={[0, 4, 4, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Certification Status */}
      <Card>
        <CardHeader>
          <CardTitle>Certification Status</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={certificationStatus}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
              <XAxis 
                dataKey="status" 
                className="text-xs"
                tick={{ fontSize: 11 }}
              />
              <YAxis className="text-xs" tick={{ fontSize: 11 }} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'hsl(var(--background))',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: '6px'
                }}
              />
              <Bar 
                dataKey="count" 
                fill={COLORS.primary}
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
};